import React, { useMemo, useState } from "react";
import { Button, Grid, ButtonGroup, Tooltip } from "@mui/material";
import { IClientSettings, ICrewGroupReleaseSchedule } from "@vmosolutions/oc-interfaces";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { FormikDateTimeField } from "src/components/MUI5FormikComponents/FormikWrappers";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { updateCrewGroupReleaseScheduleClientSetting } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { SwitchField } from "src/components/MUI5FormikComponents/SwitchField";

interface ICrewScheduleFormProps {
  crewGroups: string[];
  crewGroupReleaseScheduleSetting: IClientSettings;
  setActiveModal: (activeModal: IActiveModal) => void;
  height: number;
}

export type CrewGroupReleaseScheduleForm = FormikProps<ICrewGroupReleaseSchedule>;

export const CrewScheduleForm = (props: ICrewScheduleFormProps): JSX.Element => {
  const { crewGroups, crewGroupReleaseScheduleSetting, height } = props;
  const [copiedDate, setCopiedDate] = useState<number | null>(null);
  const [showEmptyDates, setShowEmptyDates] = useState<boolean>(true);
  const initialValues: ICrewGroupReleaseSchedule = useMemo(() => {
    return crewGroupReleaseScheduleSetting?.setValue ?? {};
  }, [crewGroupReleaseScheduleSetting]);

  const handleSubmit = async (values: any, formikHelpers: FormikHelpers<ICrewGroupReleaseSchedule>): Promise<void> => {
    const { setStatus, setSubmitting, resetForm } = formikHelpers;
    if (!crewGroupReleaseScheduleSetting?._id) {
      console.error("No crew setting found with that id");
      return;
    }
    const response = await updateCrewGroupReleaseScheduleClientSetting(values);
    if (response.setValue) {
      resetForm({ values: response?.setValue });
      setStatus({ success: true });
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleSubmit}>
      {(crewScheduleForm: CrewGroupReleaseScheduleForm) => {
        const { dirty, isValid, isSubmitting, handleReset } = crewScheduleForm;

        const handleCopyDate = (dateField: string): void => {
          setCopiedDate(crewScheduleForm.values[dateField]);
          return;
        };

        const handlePasteDate = (dateField: string): void => {
          crewScheduleForm.setFieldValue(dateField, copiedDate);
          crewScheduleForm.setFieldTouched(dateField, true);
        };

        return (
          <Form>
            <Grid container columnSpacing={2}>
              <Grid item xs={12}>
                <SwitchField
                  label="Hide Empty Dates"
                  value={showEmptyDates}
                  onChange={() => setShowEmptyDates(!showEmptyDates)}
                />
              </Grid>
              <Grid
                container
                item
                xs={12}
                sx={{ minWidth: "350px", maxHeight: `calc(${height}px - 140px)`, overflowY: "scroll" }}
              >
                {crewGroups
                  .filter((cg: string) => {
                    if (!showEmptyDates) {
                      return true;
                    }
                    return crewScheduleForm.values[cg];
                  })
                  .map((crewGroup: string, key: number) => {
                    return (
                      <Grid container key={key}>
                        <Grid item xs={10}>
                          <FormikDateTimeField
                            label={crewGroup}
                            name={crewGroup}
                            hideTimeControl={true}
                            isEpoch={true}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          sx={{
                            display: "flex",
                            justifyContent: "space=between",
                          }}
                        >
                          <Tooltip title="Copy" enterDelay={2000}>
                            <ContentCopyIcon
                              fontSize="small"
                              sx={{ cursor: "pointer", mr: 1 }}
                              onClick={() => handleCopyDate(crewGroup)}
                            />
                          </Tooltip>

                          {!!copiedDate && (
                            <Tooltip title="Paste" enterDelay={2000}>
                              <ContentPasteIcon
                                fontSize="small"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handlePasteDate(crewGroup)}
                              />
                            </Tooltip>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
              </Grid>
              <Grid item xs={12}>
                <ButtonGroup variant="contained" size="small" sx={{ mt: 3 }}>
                  <Button
                    type="reset"
                    color="warning"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                    Save
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
